function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo, useRef } from 'react';
import { compact } from 'lodash-es';
import { Controller, useFieldArray, useForm, FormProvider } from 'react-hook-form';
import { Button, Callout, FullPageModal, useOpenUnsavedChangesModal, Separator } from 'seagull';
import { useStore, useStoreActions } from "../../contexts";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useOnMount } from "../../hooks/useOnMount";
import { useUpdateRelationship } from "../../hooks/useUpdateRelationship";
import { handlePromise, scrollIntoView } from "../../infra/utils";
import { agreementChangeModel, relationshipModel, serviceModel } from "../../models";
import { AgreementDataModel } from "../../models/AgreementDataModel";
import { useValidateService } from "../../models/service/hooks/useValidateService";
import { fromDateStampObject } from "../../models/time";
import { useAppConfig } from "../../store/store-context";
import { helpArticles } from "../../utils/helpArticles";
import { makeSentryIgnoredError } from "../ErrorBoundaryPage";
import ExternalLinkButton from "../ExternalLinkButton";
import { ServicesListStructure } from "../ServicesListStructure/ServicesListStructure";
import { EditAllowManualPayments, EditAmendmentsApprovalMethod, EditCCFees, EditInvoicesApprovalMethod, EditNetTerms, Section } from "./EditAgreementComponents";
import styles from "./EditAgreementModal.module.scss";
import EditAgreementService from "./EditAgreementService/EditAgreementService";
import { EditLegalTerms } from "./EditLegalTerms/EditLegalTerms";
import { getApprovalCalloutText, makeServiceQboClassChanges, willEmailBeSent } from "./utils/EditAgreementModal.utils";
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
export const autoApprovalHelpUrl = helpArticles.amendmentsRequiringApproval();
/**
 * @deprecated Use `useTestIds` instead
 */
export const getTestPrefix = idx => "service-".concat(idx);
export default function EditAgreementModal(props) {
  const {
    relationship,
    dismissModal,
    registerCanDismiss,
    onUpdate
  } = props;
  const {
    logBI,
    fetchLegalTerms,
    fetchQboClasses
  } = useStoreActions();
  const {
    SERVICE_TEMPLATE_BUNDLE = false
  } = useFeatureFlags();
  const qboClasses = useStore(state => state.qboClasses.data);
  const {
    disableScrollAnimation
  } = useAppConfig();
  const openUnsavedChangesModal = useOpenUnsavedChangesModal();
  const updateRelationship = useUpdateRelationship();
  const approvalCalloutText = getApprovalCalloutText(relationship.amendmentsApprovalMethod);
  const onClose = () => dismissModal();
  const {
    isInvalid
  } = useValidateService();
  const {
    error: legalTermsError
  } = useStore(state => state.legalTerms);
  const servicesRef = useRef({});
  const scrollToServiceCounter = useRef(1);
  const originalServicesMap = useMemo(() => new Map(relationship.services.map(s => [s.id, s])), [relationship.services]);
  const form = useForm({
    mode: 'all',
    defaultValues: {
      updatedNetTerms: relationshipModel.actualNetTerms(relationship),
      services: relationship.services,
      selectedLegalTerms: relationship.legalTermsDocuments,
      amendmentsApprovalMethod: relationshipModel.actualAmendmentsApprovalMethod(relationship),
      invoicesApprovalMethod: relationshipModel.actualInvoicesApprovalMethod(relationship),
      allowManualPayments: relationshipModel.actualAllowManualPayments(relationship),
      coverCreditCardFees: relationshipModel.actualIsVendorCoveringCCFee(relationship)
    }
  });
  const {
    control,
    formState,
    watch,
    setValue,
    handleSubmit,
    getValues
  } = form;
  const {
    append,
    remove,
    update
  } = useFieldArray({
    control,
    name: 'services'
  });
  const [updatedNetTerms, services, selectedLegalTerms, amendmentsApprovalMethod, invoicesApprovalMethod, allowManualPayments, coverCreditCardFees] = watch(['updatedNetTerms', 'services', 'selectedLegalTerms', 'amendmentsApprovalMethod', 'invoicesApprovalMethod', 'allowManualPayments', 'coverCreditCardFees']);
  const isSelectedLegalTermsDirty = formState.dirtyFields.selectedLegalTerms === true;
  useOnMount(() => {
    fetchLegalTerms();
    fetchQboClasses();
  });
  const submitText = useMemo(() => {
    return willEmailBeSent({
      services,
      originalServicesMap,
      relationship,
      updatedNetTerms,
      isSelectedLegalTermsDirty,
      amendmentsApprovalMethod,
      invoicesApprovalMethod,
      allowManualPayments,
      coverCreditCardFees,
      serviceTemplateBundleEnabled: SERVICE_TEMPLATE_BUNDLE
    }) ? 'Save changes and notify client' : 'Save changes';
  }, [services, originalServicesMap, relationship, updatedNetTerms, isSelectedLegalTermsDirty, amendmentsApprovalMethod, invoicesApprovalMethod, allowManualPayments, coverCreditCardFees, SERVICE_TEMPLATE_BUNDLE]);
  const agreementData = AgreementDataModel.fromRelationship(relationship).setNetTerms(updatedNetTerms);
  registerCanDismiss(() => formState.isDirty ? openUnsavedChangesModal({
    testid: 'edit-agr-confirm-cancel'
  }) : Promise.resolve(true));
  function onSubmit() {
    logBI(11001);
    updateRelationship(_objectSpread(_objectSpread({}, relationship), {}, {
      changes: compact([agreementChangeModel.makeServicesChange(services.map(s => {
        const withChanges = serviceModel.applyChanges(s);
        return serviceModel.toDBService(withChanges, {
          note: serviceModel.note(s)
        });
      })), formState.dirtyFields.amendmentsApprovalMethod && agreementChangeModel.makeAmendmentsApprovalMethodChange(amendmentsApprovalMethod), formState.dirtyFields.invoicesApprovalMethod && agreementChangeModel.makeInvoicesApprovalMethodChange(invoicesApprovalMethod), formState.dirtyFields.updatedNetTerms && agreementChangeModel.makeNetTermsChange(updatedNetTerms), formState.dirtyFields.selectedLegalTerms && selectedLegalTerms &&
      // this is safe because we validate the selectedLegalTerms in the EditLegalTerms controller level
      agreementChangeModel.makeLegalTermsChange(selectedLegalTerms), formState.dirtyFields.allowManualPayments && agreementChangeModel.makeManualPaymentsChange(allowManualPayments), formState.dirtyFields.coverCreditCardFees && agreementChangeModel.makeCoverCreditCardFeesChange(coverCreditCardFees), ...makeServiceQboClassChanges(services, relationship.services, qboClasses)]),
      onSuccess: async () => {
        await handlePromise(onUpdate());
        dismissModal(undefined, {
          force: true
        });
      }
    }));
  }
  function onServiceChange(serviceId, updater) {
    const currentServices = getValues('services');
    const idx = currentServices.findIndex(s => s.id === serviceId);
    if (idx === -1) return;
    const nextService = updater(currentServices[idx]);
    setValue("services.".concat(idx), nextService, {
      shouldDirty: true,
      shouldValidate: true
    });
    update(idx, nextService);
    return nextService;
  }
  function onRemoveService(serviceId) {
    const idx = services.findIndex(s => s.id === serviceId);
    remove(idx);
  }
  async function handleServicesAdded(newServices) {
    newServices.forEach(s => {
      logBI(11004, {
        id: s.id,
        name: s.name
      });
    });
    append(newServices);
    scrollToServiceId(newServices[newServices.length - 1].id);
  }
  function scrollToServiceId(serviceId) {
    const serviceEl = servicesRef.current[serviceId];
    if (!serviceEl) {
      if (scrollToServiceCounter.current < 20) {
        scrollToServiceCounter.current++;
        setTimeout(() => scrollToServiceId(serviceId), 50);
      }
    } else {
      scrollToServiceCounter.current = 1;
      scrollIntoView(serviceEl, {
        disableScrollAnimation
      });
    }
  }
  function onInvalidSubmit(errors) {
    if (Array.isArray(errors.services)) {
      const firstServiceIdx = errors.services.findIndex(e => !!e);
      firstServiceIdx > -1 && scrollToServiceId(services[firstServiceIdx].id);
      return;
    }
    const errorMessageElement = document.querySelector('[aria-invalid="true"]');
    if (errorMessageElement) {
      scrollIntoView(errorMessageElement, {
        disableScrollAnimation
      });
    }
  }
  if (legalTermsError) {
    throw makeSentryIgnoredError('legalTermsError');
  }
  const validateService = service => {
    return !isInvalid(serviceModel.applyChanges(service), {
      relationshipEffectiveDate: relationship.effectiveDate ? fromDateStampObject(relationship.effectiveDate) : null,
      isTemplate: false
    });
  };
  return /*#__PURE__*/_jsx(FullPageModal, {
    showFooter: true,
    contentClassName: styles.content,
    "data-testid": "edit-agr",
    onClose: onClose,
    children: /*#__PURE__*/_jsxs(FormProvider, _objectSpread(_objectSpread({}, form), {}, {
      children: [/*#__PURE__*/_jsx(FullPageModal.HeaderStart, {
        children: /*#__PURE__*/_jsxs("div", {
          "data-testid": "edit-agr-title",
          children: ["Edit agreement with ", relationship.customer.name]
        })
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.centeredContent,
        children: [/*#__PURE__*/_jsxs(Callout, {
          "data-testid": "approval-callout",
          "data-type": approvalCalloutText.type,
          children: [approvalCalloutText.text, ' ', /*#__PURE__*/_jsx(ExternalLinkButton, {
            openInNewTab: true,
            underline: "hover",
            href: autoApprovalHelpUrl,
            testid: "approval-help",
            children: "Learn more"
          })]
        }), /*#__PURE__*/_jsx(Section, {
          title: "Services provided",
          children: /*#__PURE__*/_jsx(ServicesListStructure, {
            agreementData: agreementData,
            addServicesTestid: "add-services",
            onServicesAdded: handleServicesAdded,
            children: services.map((s, idx) => {
              const originalService = originalServicesMap.get(s.id);
              const serviceAlreadyTerminated = serviceModel.isTerminated(s.status) && !!originalService && serviceModel.isTerminated(originalService.status);
              return !serviceModel.isCanceled(s.status) && !serviceModel.isCompleted(s.status) && !serviceAlreadyTerminated && /*#__PURE__*/_jsx(Controller, {
                control: control,
                name: "services.".concat(idx),
                rules: {
                  validate: validateService
                },
                render: _ref => {
                  let {
                    fieldState,
                    formState
                  } = _ref;
                  return /*#__PURE__*/_jsx(EditAgreementService, {
                    service: s,
                    idx: idx,
                    testId: getTestPrefix(idx),
                    ref: el => servicesRef.current[s.id] = el,
                    relationship: relationship,
                    agreementData: agreementData,
                    originalService: originalService,
                    showServiceValidationWarning: !!fieldState.error && formState.isSubmitted,
                    onServiceChange: onServiceChange,
                    onRemoveService: onRemoveService
                  });
                }
              }, s.id);
            })
          })
        }), /*#__PURE__*/_jsxs(Section, {
          title: "Payment settings",
          children: [/*#__PURE__*/_jsx(EditNetTerms, {
            relationship: relationship
          }), /*#__PURE__*/_jsx(EditCCFees, {
            relationship: relationship
          }), /*#__PURE__*/_jsx(EditAllowManualPayments, {
            relationship: relationship
          })]
        }), /*#__PURE__*/_jsxs(Section, {
          title: "Agreement terms",
          children: [/*#__PURE__*/_jsx(EditLegalTerms, {
            relationship: relationship
          }), /*#__PURE__*/_jsx(Separator, {
            spacing: "none"
          }), /*#__PURE__*/_jsx(EditAmendmentsApprovalMethod, {
            relationship: relationship
          }), /*#__PURE__*/_jsx(EditInvoicesApprovalMethod, {
            relationship: relationship
          })]
        })]
      }), /*#__PURE__*/_jsxs(FullPageModal.FooterEnd, {
        children: [/*#__PURE__*/_jsx(Button, {
          skin: "primaryOutline",
          "data-testid": "edit-agr-cancel",
          onClick: onClose,
          children: "Cancel"
        }), /*#__PURE__*/_jsx(Button, {
          disabled: !formState.isDirty,
          "data-testid": "edit-agr-submit",
          onClick: handleSubmit(onSubmit, onInvalidSubmit),
          children: submitText
        })]
      })]
    }))
  });
}